import { useEffect, useState } from 'react';
import { Mail, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2M PR 3 — Weekly Digest preview card.
 *
 * Renders the latest persisted weekly digest as an editorial in-app preview.
 * Falls back gracefully (renders nothing) when no digest exists yet.
 *
 *   <ArenaxDigestPreview city="Bengaluru" sport="Football" />
 */
export default function ArenaxDigestPreview({ city, sport }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    if (sport) params.set('sport', sport);
    api.get(`/arenax/digests/weekly?${params.toString()}`)
      .then(r => setDigest(r.data?.digest || null))
      .catch(() => setDigest(null))
      .finally(() => setLoading(false));
  }, [city, sport]);

  if (loading || !digest || digest.empty || !(digest.sections || []).length) return null;

  const composedAt = digest.composed_at
    ? new Date(digest.composed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="rounded-2xl border border-amber-500/30 p-5 mb-6"
      data-testid="arenax-digest-preview"
      style={{ background: 'linear-gradient(160deg, rgba(245,158,11,0.08), var(--ax-card-bg-soft))' }}>
      <header className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-amber-300" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">
              Weekly Digest
            </div>
            <h3 className="text-base sm:text-lg font-black text-slate-100 leading-tight">
              {digest.subject}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {composedAt && (
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{composedAt}</span>
          )}
          <button onClick={() => setExpanded(!expanded)}
            data-testid="arenax-digest-toggle"
            className="p-1.5 rounded-md hover:bg-slate-800/60 text-slate-400 hover:text-slate-100 transition-colors">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </header>
      {expanded && (
        <div className="space-y-3 pt-2 border-t border-slate-800/60">
          <p className="text-xs italic text-slate-400">{digest.intro}</p>
          {(digest.sections || []).map((s, i) => (
            <p key={i} className="text-sm text-slate-200 leading-snug"
              data-testid={`arenax-digest-section-${s.kind}`}>
              {s.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
